import { useMemo } from "react";
import useSWR from "swr";
import { MapPin, CalendarClock } from "lucide-react";
import { photoUrl } from "@/lib/api";
import { compute } from "@/lib/finance";
import { usd, num, usdCompact } from "@/lib/format";
import { Card, Stat, IronDivider } from "@/components/common/Ornament";
import { RichHtml } from "@/components/common/RichText";
import ProjectTimeline from "@/components/overview/ProjectTimeline";

function Hero({ c }) {
  return (
    <section className="relative overflow-hidden rounded-xl min-h-[380px] sm:min-h-[460px] flex items-end fade-up" data-testid="overview-hero">
      {c.hero_photo_id && <img src={photoUrl(c.hero_photo_id)} alt={c.project_name} className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15,46,35,.94) 0%, rgba(15,46,35,.72) 45%, rgba(15,46,35,.15) 100%)" }} />
      <div className="relative p-6 sm:p-10 lg:p-14 max-w-2xl text-[#F9F6F0]">
        <p className="eyebrow !text-[#D4AF37]">Investment Opportunity · New Orleans, Louisiana</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-4" data-testid="overview-hero-title">{c.project_name}</h1>
        <p className="mt-4 text-base sm:text-lg text-[#F9F6F0]/85">{c.tagline}</p>
        <p className="mt-6 flex items-center gap-2 text-sm text-[#F9F6F0]/70" data-testid="overview-address"><MapPin className="h-4 w-4 text-[#D4AF37]" /> {c.address}</p>
      </div>
    </section>
  );
}

export default function Overview() {
  const { data: c } = useSWR("/content");
  const { data: fin } = useSWR("/financials");
  const r = useMemo(() => (fin ? compute(fin.baseline) : null), [fin]);
  if (!c) return <div className="h-96 rounded-xl bg-white/60 animate-pulse" />;

  return (
    <div className="space-y-10">
      <Hero c={c} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="overview-stats">
        <Stat dark label="Room Count" value={r ? num(r.rooms) : "—"} sub="Keys" testId="overview-stat-keys" />
        <Stat label="Total Project Cost" value={r ? usdCompact(r.totalCost) : "—"} sub={r ? usd(r.totalCost) : ""} testId="overview-stat-cost" />
        <Stat label="Cost per Key" value={r ? usd(r.costPerKey) : "—"} sub="Baseline model" testId="overview-stat-cost-per-key" />
        <Stat label="Target Opening" value={c.target_opening || "TBD"} sub={<span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Projected</span>} testId="overview-stat-opening" />
      </div>
      {c.highlights?.length > 0 && (
        <div className="flex flex-wrap gap-x-10 gap-y-4 border-y border-[#E2DCD2] py-5" data-testid="overview-highlights">
          {c.highlights.map((h, i) => (
            <div key={i} data-testid={`overview-highlight-${i}`}>
              <p className="text-[0.68rem] uppercase tracking-[0.18em] text-[#3E5C4E] font-semibold">{h.label}</p>
              <p className="font-display text-2xl text-[#0F2E23] font-semibold">{h.value}</p>
            </div>
          ))}
        </div>
      )}
      <ProjectTimeline items={c.timeline} />
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-7" eyebrow="The Opportunity" title="Executive Summary" testId="overview-exec-summary">
          <RichHtml html={c.exec_summary} />
        </Card>
        <div className="lg:col-span-5 space-y-6">
          <Card eyebrow="Where Things Stand" title="Development Status" testId="overview-status">
            <RichHtml html={c.status_html} />
          </Card>
          {r && (
            <Card eyebrow="Baseline" title="Capital Structure" testId="overview-capital">
              <div className="space-y-3 text-sm">
                {[["Total project cost", r.totalCost], ["Senior loan", r.loan], ["Equity required", r.equity]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-dashed border-[#E2DCD2] pb-2"><span className="text-slate-600">{k}</span><span className="font-mono-num font-semibold text-[#0F2E23]">{usd(v)}</span></div>
                ))}
              </div>
              <IronDivider className="mt-5" />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
